/** The tabs of the tenant's Settings page, the first one the default. */
export const SETTINGS_TABS = ['general', 'configurations', 'members'] as const
export type SettingsTab = (typeof SETTINGS_TABS)[number]

const DEFAULT_TAB: SettingsTab = 'general'

/** Settings › Members: the host tenant's members and open invitations. */
export const MEMBERS_TAB_PATH = '/settings?tab=members'

/**
 * Settings › Configurations: the host tenant's default configuration of
 * each game.
 */
export const CONFIGURATIONS_TAB_PATH = '/settings?tab=configurations'

/**
 * The tabs a user sees: Configurations only when they may set the default
 * configurations, Members only when they may manage the members.
 */
export function visibleSettingsTabs({
  configurations,
  members,
}: {
  configurations: boolean
  members: boolean
}): readonly SettingsTab[] {
  return SETTINGS_TABS.filter(
    (tab) =>
      (tab !== 'configurations' || configurations) &&
      (tab !== 'members' || members),
  )
}

/**
 * The tab the URL names (`?tab=`) among `tabs`, else the default; an unknown
 * one too, and one the user does not see.
 */
export function settingsTab(
  params: URLSearchParams,
  tabs: readonly SettingsTab[] = SETTINGS_TABS,
): SettingsTab {
  const tab = params.get('tab')
  return tabs.find((known) => known === tab) ?? DEFAULT_TAB
}

/** `params` showing `tab`: no `tab` param for the default, so `/settings` stays plain. */
export function settingsTabParams(
  params: URLSearchParams,
  tab: SettingsTab,
): URLSearchParams {
  const next = new URLSearchParams(params)
  if (tab === DEFAULT_TAB) {
    next.delete('tab')
  } else {
    next.set('tab', tab)
  }
  return next
}
